import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';

import resetIcon from '/resetIcon.svg';

import MaskInput from 'components/MaskedInput';
import { BoardSteps } from 'utils/constants';
import {
  numericValues, unMaskInput, unMaskInputValue, 
} from 'utils/helpers';
import { useNextHandler } from 'features/Croptimizer/providers/NextHandlerContext';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { scrollTop } from 'utils/scroll-top';
import { formatSwissNumber } from 'utils/formatNumber';
import useCroptimizerMineralization from 'api/hooks/useCroptimizerMineralization';

import { useGermanStatusBoard } from '../1Status/German/hooks/useGermanStatusBoard';
import NpkInputSection from '../4Fertilization/components/NpkInputSection';
import PricesSection from '../4Fertilization/components/PricesSection';
import TotalGainSection from '../4Fertilization/components/TotalGainSection';
import { useFertilizationInputs } from '../4Fertilization/hooks/useFertilizationInputs';
import { resolveStoredSoPrice, useMineralizationBoard } from '../5Mineralization/hooks/useMineralizationBoard';
import { useFertilizationCalculations } from '../4Fertilization/hooks/useFertilizationCalculations';
import ExpectedGainsSection from '../4Fertilization/components/ExpectedGainsSection';

interface Props {
  isPDF?: boolean;
  isGerman: boolean;
}

export const GermanCropBoard: FC<Props> = ({ isPDF, isGerman }) => {
  const {
    setStep,
    boardCropValues,
    setBoardCropValues,
    step,
    mtHa,
    onlyMt: mt,
    onlyAc,
    kgHa,
    kg,
    setFertilizationValues,
    setMineralizationValues,
    setActiveTab,
    isExpertMode,
  } = useCroptimizer();

  const {
    initialPrice,
    getErrorOfPrice,
    getYieldGain,
    getYieldGainPrice,
    cropGroupYield,
    cleanprice,
    getYieldGainMtha,
    cleancurrentyield,
    price,
    setPrice,
    resetData: resetDataCrop,
  } = useGermanStatusBoard();

  const isActive = step === BoardSteps.crop;

  const handleNextStep = () => {
    if (isExpertMode) {
      setStep(BoardSteps.fertilization);
    } else {
      setActiveTab(2);
    }
  };

  const handleBackStep = () => {
    setStep(BoardSteps.status);
    scrollTop();
  };

  const { t } = useTranslation();
  const { setHandler, setShowBack, setBackHandler, setShowNext, setDisabled } =
    useNextHandler();

  const { currencyGreen } = useCurrency();

  // fertilization
  const {
    inputN,
    setInputN,
    cleaninputn,
    inputP,
    setInputP,
    cleaninputp,
    inputK,
    setInputK,
    cleaninputk,
    pricesN,
    setPricesN,
    cleanpricesn,
    pricesP,
    setPricesP,
    cleanpricesp,
    pricesK, 
    setPricesK,
    cleanpricesk,
    efficiencyN,
    setEfficiencyN,
    cleaneffiN,
    efficiencyP,
    setEfficiencyP,
    cleaneffiP,
    efficiencyK,
    setEfficiencyK,
    cleaneffiK,
    resetData: resetDataFertilization,
  } = useFertilizationInputs();

  // mineralization
  const {
    cap,
    organicMatter,
    setOrganicMatter,
    errorOrganicMatter,
    cleanPhAvg,
    errorPhAvg,
    errorSoilTillage,
    month,
    soilTillage,
    cleanorganicmatter,
    cleanSoPrice,
  } = useMineralizationBoard(isGerman);
  const { data: mineralization } = useCroptimizerMineralization();
  
  useEffect(() => {
    // When active, configure navigation buttons
    // and define the "Next" handler to update board state and trigger status handling
    if (!isActive) {
      return;
    }

    setShowBack(true);
    setShowNext(true);
    setBackHandler(handleBackStep);
    setDisabled(!!errorPhAvg || !!errorOrganicMatter || !!errorSoilTillage || !!getErrorOfPrice);

    setHandler(() => {
      setBoardCropValues({
        crop: boardCropValues?.crop || '',
        yieldGain: getYieldGain,
        yieldGainHa: getYieldGainPrice,
        cropGroupYield,
        currentYield: unMaskInput(cleancurrentyield ?? ''),
        price: unMaskInputValue(cleanprice),
      });

      setFertilizationValues({
        nvalue: unMaskInputValue(String(cleaninputn)),
        pvalue: unMaskInputValue(String(cleaninputp)),
        kvalue: unMaskInputValue(String(cleaninputk)),
        nprice: unMaskInputValue(String(cleanpricesn)),
        pprice: unMaskInputValue(String(cleanpricesp)),
        kprice: unMaskInputValue(String(cleanpricesk)),
        efficiencyN: unMaskInputValue(String(efficiencyN)),
        efficiencyP: unMaskInputValue(String(efficiencyP)),
        efficiencyK: unMaskInputValue(String(efficiencyK)),
      });

      const nrate =
        Number(numericValues(mineralization?.[0]?.NRate ?? '')) / 100;

      const prate =
        Number(numericValues(mineralization?.[0]?.P2O5Rate ?? '')) / 100;

      const srate =
        Number(numericValues(mineralization?.[0]?.SO3Rate ?? '')) / 100;

      setMineralizationValues({
        annualDecrease: cleanPhAvg,
        cap,
        month,
        soilTillage,
        organicMatter: cleanorganicmatter,
        nRate: nrate,
        pRate: prate,
        sRate: srate,
        sPrice: resolveStoredSoPrice(cleanSoPrice),
      });

      handleNextStep();

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    });
  }, [
    getErrorOfPrice,
    isActive,
    boardCropValues?.crop,
    getYieldGain,
    getYieldGainPrice,
    cropGroupYield,
    cleancurrentyield,
    cleanprice,

    // fertilization
    cleaninputn,
    cleaninputp,
    cleaninputk,
    cleanpricesn,
    cleanpricesp,
    cleanpricesk,
    efficiencyN,
    efficiencyP,
    efficiencyK,
    setHandler,
    setShowBack,
    setShowNext,
    setBackHandler,
    setFertilizationValues,

    // mineralization
    errorPhAvg,
    errorOrganicMatter,
    isActive,
    cleanPhAvg,
    cap,
    month,
    soilTillage,
    cleanorganicmatter,
    cleanSoPrice,
    mineralization,
  ]);

  const {
    targetNPercent,
    currentNPercent,
    getCurrentN,
    getTargetN,
    getTargetPPercent,
    currentPPercent,
    getCurrentP,
    getTargetP,
    getTargetKPercent,
    currentKPercent,
    getCurrentK,
    getTargetK,
  } = useFertilizationCalculations(
    inputN,
    inputP,
    inputK,
    pricesN,
    pricesP,
    pricesK,
    cleaninputn,
    cleaninputp,
    cleaninputk,
    cleanpricesn,
    cleanpricesp,
    cleanpricesk,
    setEfficiencyN,
    setEfficiencyP,
    setEfficiencyK,
  );
    
  return (
    <form>
      <div className="border border-primitiveGrey p-8 md:px-4 rounded-2xl">
        {isExpertMode &&
          <div
            className={'border-b border-primitiveGrey pb-8 break-inside-avoid'}
          >
            <div className="text-black font-bold text-xl md:text-lg mb-8">
              {t('potentialProductivityGain')}
            </div>
            <div className="flex mx-[-0.5rem]">
              <div className="w-1/2 px-2 break-inside-avoid">
                <MaskInput
                  titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
                  title={t('yieldGain') + ' (%)'}
                  className="w-full"
                  disabled
                  value={formatSwissNumber(Number(getYieldGain))}
                />
              </div>
              <div className="w-1/2 px-2 break-inside-avoid">
                <MaskInput
                  titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
                  title={t('yieldGain') + ` (${mtHa})`}
                  className="w-full"
                  disabled
                  value={formatSwissNumber(Number(getYieldGainMtha))}
                />
              </div>
            </div>
          </div>
        }
        <div className={`${isExpertMode ? 'py-8' : 'pt-2 pb-8 mb-8 border-b border-primitiveGrey'} break-inside-avoid`}>
          <div className="text-black font-bold text-xl md:text-lg mb-8">
            {t('economicProductivityGain')}
          </div>
          <div className="flex mx-[-0.5rem]">
            <div className="w-1/2 px-2 break-inside-avoid">
              <MaskInput
                titleClassName="top-[-9px] left-[8px] truncate max-w-[85%]"
                title={t('pricePerTon') + ` (${currencyGreen}/${mt})`}
                className="w-full"
                isGreyText={initialPrice === price}
                onChange={setPrice}
                value={price}
                redBorder={!!getErrorOfPrice}
              />

              <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
                {!!getErrorOfPrice && <div>{getErrorOfPrice}</div>}
              </div>
            </div>
            {isExpertMode &&
              <div className="w-1/2 px-2 break-inside-avoid">
                <MaskInput
                  titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
                  title={t('yieldGain') + ` (${currencyGreen}/${onlyAc})`}
                  className="w-full"
                  disabled
                  value={formatSwissNumber(Number(getYieldGainPrice))}
                />
              </div>
            }
          </div>
        </div>

        {/* fertilisation */}
        {!isExpertMode &&
          <div className='pt-2 border-b border-primitiveGrey break-inside-avoid'>
            <NpkInputSection
              inputN={inputN}
              setInputN={setInputN}
              cleaninputn={cleaninputn}
              inputP={inputP}
              setInputP={setInputP}
              cleaninputp={cleaninputp}
              inputK={inputK}
              setInputK={setInputK}
              cleaninputk={cleaninputk}
              kgHa={kgHa}
            />

            {/* need this for PricesSection and  TotalGainSection calculation */}
            {false &&
              <ExpectedGainsSection
                getCurrentN={getCurrentN}
                currentNPercent={currentNPercent}
                getCurrentP={getCurrentP}
                currentPPercent={currentPPercent}
                getCurrentK={getCurrentK}
                currentKPercent={currentKPercent}
                getTargetN={getTargetN}
                targetNPercent={targetNPercent}
                getTargetP={getTargetP}
                getTargetPPercent={getTargetPPercent}
                getTargetK={getTargetK}
                getTargetKPercent={getTargetKPercent}
                kgHa={kgHa}
              />
            }
        
            <PricesSection
              pricesN={pricesN}
              setPricesN={setPricesN}
              pricesP={pricesP}
              setPricesP={setPricesP}
              pricesK={pricesK}
              setPricesK={setPricesK}
              efficiencyN={efficiencyN}
              setEfficiencyN={setEfficiencyN}
              efficiencyP={efficiencyP}
              setEfficiencyP={setEfficiencyP}
              efficiencyK={efficiencyK}
              setEfficiencyK={setEfficiencyK}
              kg={kg}
              onlyAc={onlyAc}
            />

            <TotalGainSection
              cleaneffiN={cleaneffiN}
              cleaneffiP={cleaneffiP}
              cleaneffiK={cleaneffiK}
              onlyAc={onlyAc}
            />
          </div>
        }
        
        {/* mineralization */}
        {!isExpertMode &&
          <div className="py-8">
            <div className="text-black font-bold text-xl md:text-lg mb-8">
              {t('mineralizationGain')}
            </div>
            <div className="flex mx-[-0.5rem] mb-4 break-inside-avoid">
              <div className="w-1/3 px-2">
                <MaskInput
                  titleClassName="top-[-9px] left-[8px] truncate max-w-[85%]"
                  title={t('organicMatter')}
                  className="w-full"
                  value={organicMatter}
                  onChange={setOrganicMatter}
                  redBorder={!!errorOrganicMatter}
                  suffix=" %"
                />
                <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
                  {errorOrganicMatter && <div> {errorOrganicMatter}</div>}
                </div>
              </div>
            </div>
          </div>
        }
        
        {!isPDF && (
          <div className="flex items-center justify-center font-medium text-primary">
            <div
              className="cursor-pointer flex items-center"
              onClick={() => {
                resetDataCrop();
                !isExpertMode && resetDataFertilization();
              }}
            >
              <span className="mr-3">
                <img src={resetIcon} alt="" />
              </span>
              {t('resetData')}
            </div>
          </div>
        )}
      </div>
    </form>
  );
};
